import { useState } from 'react';
import { TPaymentIntent } from '@xeppt/xeppt-sdk/types';
import { useApiQuery } from '@hooks/api/useApiQuery';
import { useLocales } from '@hooks/helpers/useLocales';
import { useGlobalModals } from '@hooks/modals/useGlobalModals';
import useModalContext from '@hooks/context/useModalContext';
import { apiAdminService } from '@api';
import { removeEmptyArrays } from '@utils/index';
import { TPaymentIntentsFilter } from '@xeppt/xeppt-sdk/types';
import { toast } from 'react-toastify';
import { TPaymentIntentWithReferenceCode } from '@reference_code';

interface IProps {
    isInitialIntents?: boolean;
}

export const useExternalPayments = ({ isInitialIntents }: IProps) => {
    const [cursor, setCursor] = useState<Date>();
    const [intentPayments, setIntentPayments] = useState<
        (TPaymentIntent & {
            user_id: string;
        })[]
    >([]);
    const { handleOpenApproveActionModal } = useGlobalModals();
    const { onClose } = useModalContext();
    const { requestErrorLocale, requestSuccessLocale } = useLocales();
    const [filter, setFilter] = useState<TPaymentIntentsFilter>({
        cursor: new Date(),
        limit: 100,
        date_from: undefined,
        date_to: undefined,
        type: [],
        status: [],
        amount_from: undefined,
        amount_to: undefined,
        order: 'DESC'
    });

    const { data: intentsData, handleRequest: refetchIntents } = useApiQuery({
        method: (request?: any) => {
            console.log({
                ...removeEmptyArrays(request?.filter),
                cursor: request?.isNew
                    ? request?.filter?.order === 'DESC'
                        ? new Date()
                        : new Date('2024-01-01')
                    : cursor || new Date()
            });
            return apiAdminService.getPaymentIntents({
                ...removeEmptyArrays(request?.filter),
                cursor: request?.isNew
                    ? request?.filter?.order === 'DESC'
                        ? new Date()
                        : new Date('2024-01-01')
                    : cursor || new Date()
            });
        },
        onSuccess: (data, request?: any) => {
            if (cursor !== data.cursor && cursor && !request?.isNew) {
                setIntentPayments((state) => [...state, ...data.data]);
                setCursor(data.cursor);
            } else {
                setIntentPayments(data.data);
                setCursor(data.cursor);
            }
        },
        isInitialRequest: isInitialIntents
    });

    const onChangeFilter = (data: Partial<TPaymentIntentsFilter>) => {
        setCursor(undefined);
        setFilter((state) => ({ ...state, ...data }));
        refetchIntents({ isNew: true, filter: { ...filter, ...data } });
    };

    const {
        data: intent,
        handleRequest: getSingleIntent,
        handleRemoveResponse: handleRemoveIntentData
    } = useApiQuery<string, TPaymentIntentWithReferenceCode>({
        method: (id?: string) => apiAdminService.getPaymentIntentById(id || ''),
        isInitialRequest: false
    });

    const { handleRequest: handleCancelIntent } = useApiQuery({
        method: (id?: string) => apiAdminService.cancelPaymentIntent(id || ''),
        isInitialRequest: false,
        onSuccess: () => {
            onClose();
            toast.success(requestSuccessLocale('cancel_intent'));
            refetchIntents({ isNew: true, filter });
        },
        onError: (e: any) => {
            const status: number = e?.statusCode ?? 0;
            // 409 Conflict or 422 Unprocessable = backend rejects canceling a non-pending intent
            if (status === 409 || status === 422) {
                toast.error('This payment can no longer be canceled.');
            } else {
                requestErrorLocale(e);
            }
        }
    });

    const handleOpenChangeStatusModal = (id: string) => {
        handleOpenApproveActionModal({
            title: 'Cancel external payment',
            description: `Are you sure you want to cancel external payment?`,
            onSubmit: () => handleCancelIntent(id),
            cancelLabel: 'cancel',
            confirmLabel: 'confirm'
        });
    };

    return {
        intentPayments,
        intentsData,
        filter,
        refetchIntents,
        onChangeFilter,
        intent,
        getSingleIntent,
        handleRemoveIntentData,
        handleCancelIntent: handleOpenChangeStatusModal
    };
};
